import { DataSyncMetricsBucket } from '../../../types/DataSyncMetrics';
import {
  BucketPoint,
  buildBucketPointsForRange,
  buildBucketSeriesForRange,
  MetricsGranularity,
} from '../../../lib/metricsUtils';
import { buildPeriodChange, formatSignedInteger, toNumber } from './analyticsMath';

export type StageCardKey =
  | 'received'
  | 'delivered'
  | 'ingest'
  | 'load'
  | 'filter'
  | 'mapping'
  | 'delivery';

export interface StageCardModel {
  key: StageCardKey;
  name: string;
  series: number[];
  trend: {
    delta: number;
  };
  value: number;
}

const isOverviewStageKey = (key: StageCardKey) =>
  key === 'received' || key === 'delivered';

// The stage grid shows only the per-stage drop-off cards (overview cards are
// rendered separately). The FILTER card is additionally gated behind the
// multi-language feature flag.
export const getVisibleStageCards = (
  cards: StageCardModel[],
  multiLanguageEnabled: boolean,
): StageCardModel[] =>
  cards.filter((card) => {
    if (isOverviewStageKey(card.key)) {
      return false;
    }
    if (card.key === 'filter' && !multiLanguageEnabled) {
      return false;
    }
    return true;
  });

interface BuildAnalyticsViewModelInput {
  buckets: DataSyncMetricsBucket[];
  previousBuckets: DataSyncMetricsBucket[];
  selectedStageKey: StageCardKey;
  fromEpoch: number;
  toEpoch: number;
  granularity: MetricsGranularity;
}

interface BuildAnalyticsViewModelOutput {
  cards: StageCardModel[];
  totalReceived: number;
  totalDelivered: number;
  receivedTrend: { delta: number };
  deliveredTrend: { delta: number };
  receivedSeries: number[];
  deliveredSeries: number[];
  detailSeries: BucketPoint[];
  detailTitle: string;
  detailDescription: string;
  detailValueLabel: string;
  detailPrimaryValue: number;
  detailSecondaryLine: string;
  detailFootnote?: string;
}

const filterStageBuckets = (
  buckets: DataSyncMetricsBucket[],
  stage: 'ingest' | 'load' | 'filter' | 'map' | 'export',
) => buckets.filter((bucket) => bucket.stage === stage);

export const buildAnalyticsViewModel = ({
  buckets,
  previousBuckets,
  selectedStageKey,
  fromEpoch,
  toEpoch,
  granularity,
}: BuildAnalyticsViewModelInput): BuildAnalyticsViewModelOutput => {
  const ingestBuckets = filterStageBuckets(buckets, 'ingest');
  const loadBuckets = filterStageBuckets(buckets, 'load');
  const filterStageBucketsList = filterStageBuckets(buckets, 'filter');
  const mappingBuckets = filterStageBuckets(buckets, 'map');
  const exportBuckets = filterStageBuckets(buckets, 'export');

  // The filter stage card shows total items the filter evaluated — the sum of
  // items dropped (success) and items where evaluation errored. The footnote
  // breaks out just the error count when non-zero.
  const filterBucketTotal = (bucket: DataSyncMetricsBucket) =>
    toNumber(bucket.total_success_count) + toNumber(bucket.total_error_count);

  const totalFilterEvaluated = filterStageBucketsList.reduce(
    (sum, bucket) => sum + filterBucketTotal(bucket),
    0,
  );
  const totalFilterEvalErrors = filterStageBucketsList.reduce(
    (sum, bucket) => sum + toNumber(bucket.total_error_count),
    0,
  );

  const previousIngestBuckets = filterStageBuckets(previousBuckets, 'ingest');
  const previousLoadBuckets = filterStageBuckets(previousBuckets, 'load');
  const previousFilterBuckets = filterStageBuckets(previousBuckets, 'filter');
  const previousMappingBuckets = filterStageBuckets(previousBuckets, 'map');
  const previousExportBuckets = filterStageBuckets(previousBuckets, 'export');

  const ingestSuccessSeries = buildBucketSeriesForRange(
    ingestBuckets,
    (bucket) => toNumber(bucket.total_success_count),
    fromEpoch,
    toEpoch,
    granularity,
  );
  const exportSuccessSeries = buildBucketSeriesForRange(
    exportBuckets,
    (bucket) => toNumber(bucket.total_success_count),
    fromEpoch,
    toEpoch,
    granularity,
  );

  const ingestErrorSeries = buildBucketSeriesForRange(
    ingestBuckets,
    (bucket) => toNumber(bucket.total_error_count),
    fromEpoch,
    toEpoch,
    granularity,
  );
  const loadErrorSeries = buildBucketSeriesForRange(
    loadBuckets,
    (bucket) => toNumber(bucket.total_error_count),
    fromEpoch,
    toEpoch,
    granularity,
  );
  const mappingErrorSeries = buildBucketSeriesForRange(
    mappingBuckets,
    (bucket) => toNumber(bucket.total_error_count),
    fromEpoch,
    toEpoch,
    granularity,
  );
  const deliveryErrorSeries = buildBucketSeriesForRange(
    exportBuckets,
    (bucket) => toNumber(bucket.total_error_count),
    fromEpoch,
    toEpoch,
    granularity,
  );
  const filterEvaluatedSeries = buildBucketSeriesForRange(
    filterStageBucketsList,
    filterBucketTotal,
    fromEpoch,
    toEpoch,
    granularity,
  );

  const previousTotalFilterEvaluated = previousFilterBuckets.reduce(
    (sum, bucket) => sum + filterBucketTotal(bucket),
    0,
  );

  const totalReceived = ingestSuccessSeries.reduce(
    (sum, value) => sum + value,
    0,
  );
  const totalDelivered = exportSuccessSeries.reduce(
    (sum, value) => sum + value,
    0,
  );

  const previousTotalReceived = previousIngestBuckets.reduce(
    (sum, bucket) => sum + toNumber(bucket.total_success_count),
    0,
  );
  const previousTotalDelivered = previousExportBuckets.reduce(
    (sum, bucket) => sum + toNumber(bucket.total_success_count),
    0,
  );

  const totalFailedOnIngest = ingestErrorSeries.reduce(
    (sum, value) => sum + value,
    0,
  );
  const totalFailedOnLoad = loadErrorSeries.reduce(
    (sum, value) => sum + value,
    0,
  );
  const totalFailedOnMapping = mappingErrorSeries.reduce(
    (sum, value) => sum + value,
    0,
  );
  const totalFailedOnDelivery = deliveryErrorSeries.reduce(
    (sum, value) => sum + value,
    0,
  );

  const receivedBaseTotal = totalReceived + totalFailedOnIngest;
  const receivedSharePercent =
    receivedBaseTotal > 0 ? (totalReceived / receivedBaseTotal) * 100 : 0;
  const deliveredOfReceivedPercent =
    totalReceived > 0 ? (totalDelivered / totalReceived) * 100 : 0;

  const previousFailedOnIngest = previousIngestBuckets.reduce(
    (sum, bucket) => sum + toNumber(bucket.total_error_count),
    0,
  );
  const previousFailedOnLoad = previousLoadBuckets.reduce(
    (sum, bucket) => sum + toNumber(bucket.total_error_count),
    0,
  );
  const previousFailedOnMapping = previousMappingBuckets.reduce(
    (sum, bucket) => sum + toNumber(bucket.total_error_count),
    0,
  );
  const previousFailedOnDelivery = previousExportBuckets.reduce(
    (sum, bucket) => sum + toNumber(bucket.total_error_count),
    0,
  );

  const receivedTrend = buildPeriodChange(totalReceived, previousTotalReceived);
  const deliveredTrend = buildPeriodChange(
    totalDelivered,
    previousTotalDelivered,
  );
  const ingestErrorTrend = buildPeriodChange(
    totalFailedOnIngest,
    previousFailedOnIngest,
  );
  const loadErrorTrend = buildPeriodChange(
    totalFailedOnLoad,
    previousFailedOnLoad,
  );
  const mappingErrorTrend = buildPeriodChange(
    totalFailedOnMapping,
    previousFailedOnMapping,
  );
  const deliveryErrorTrend = buildPeriodChange(
    totalFailedOnDelivery,
    previousFailedOnDelivery,
  );
  const filterEvaluatedTrend = buildPeriodChange(
    totalFilterEvaluated,
    previousTotalFilterEvaluated,
  );

  const cards: StageCardModel[] = [
    {
      key: 'received',
      name: 'Items received',
      series: ingestSuccessSeries,
      trend: receivedTrend,
      value: totalReceived,
    },
    {
      key: 'delivered',
      name: 'Items delivered',
      series: exportSuccessSeries,
      trend: deliveredTrend,
      value: totalDelivered,
    },
    {
      key: 'ingest',
      name: 'Failed on ingest',
      series: ingestErrorSeries,
      trend: ingestErrorTrend,
      value: totalFailedOnIngest,
    },
    {
      key: 'load',
      name: 'Failed on load',
      series: loadErrorSeries,
      trend: loadErrorTrend,
      value: totalFailedOnLoad,
    },
    {
      key: 'filter',
      name: 'Filtered at source',
      series: filterEvaluatedSeries,
      trend: filterEvaluatedTrend,
      value: totalFilterEvaluated,
    },
    {
      key: 'mapping',
      name: 'Failed on mapping',
      series: mappingErrorSeries,
      trend: mappingErrorTrend,
      value: totalFailedOnMapping,
    },
    {
      key: 'delivery',
      name: 'Failed on delivery',
      series: deliveryErrorSeries,
      trend: deliveryErrorTrend,
      value: totalFailedOnDelivery,
    },
  ];

  const selectedStage =
    cards.find((stage) => stage.key === selectedStageKey) || cards[0];

  const STAGE_DETAIL_COPY: Record<
    Exclude<StageCardKey, 'received' | 'delivered'>,
    { description: string; valueLabel: string }
  > = {
    ingest: {
      description: 'Items that failed during ingest over the selected date range',
      valueLabel: 'items failed',
    },
    load: {
      description: 'Items that failed during load over the selected date range',
      valueLabel: 'items failed',
    },
    filter: {
      description: 'Items the filter evaluated over the selected date range',
      valueLabel: 'items evaluated',
    },
    mapping: {
      description: 'Items that failed during mapping over the selected date range',
      valueLabel: 'items failed',
    },
    delivery: {
      description: 'Items that failed during delivery over the selected date range',
      valueLabel: 'items failed',
    },
  };

  const detailTitle =
    selectedStage.key === 'received'
      ? 'Successfully received'
      : selectedStage.key === 'delivered'
        ? 'Successfully delivered'
        : selectedStage.name;
  const detailDescription =
    selectedStage.key === 'received'
      ? 'Items successfully received from source'
      : selectedStage.key === 'delivered'
        ? 'Items successfully delivered to destination'
        : STAGE_DETAIL_COPY[selectedStage.key].description;
  const detailValueLabel =
    selectedStage.key === 'received'
      ? 'items received'
      : selectedStage.key === 'delivered'
        ? 'items delivered'
        : STAGE_DETAIL_COPY[selectedStage.key].valueLabel;
  const detailPrimaryValue = selectedStage.value;
  const detailSecondaryLine =
    selectedStage.key === 'received' || selectedStage.key === 'delivered'
      ? selectedStage.key === 'received'
        ? `${receivedSharePercent.toFixed(0)}% of total received`
        : `${deliveredOfReceivedPercent.toFixed(0)}% delivered of successfully received`
      : `Change: ${formatSignedInteger(selectedStage.trend.delta)}`;
  const STAGE_BUCKET_SOURCES: Record<StageCardKey, DataSyncMetricsBucket[]> = {
    received: ingestBuckets,
    delivered: exportBuckets,
    ingest: ingestBuckets,
    load: loadBuckets,
    filter: filterStageBucketsList,
    mapping: mappingBuckets,
    delivery: exportBuckets,
  };
  const selectedStageBuckets = STAGE_BUCKET_SOURCES[selectedStage.key];

  const detailValueSelector = (bucket: DataSyncMetricsBucket) => {
    if (selectedStage.key === 'filter') {
      return filterBucketTotal(bucket);
    }
    if (selectedStage.key === 'received' || selectedStage.key === 'delivered') {
      return toNumber(bucket.total_success_count);
    }
    return toNumber(bucket.total_error_count);
  };

  const detailSeries = buildBucketPointsForRange(
    selectedStageBuckets,
    detailValueSelector,
    fromEpoch,
    toEpoch,
    granularity,
  );

  const detailFootnote =
    selectedStage.key === 'filter' && totalFilterEvalErrors > 0
      ? `${totalFilterEvalErrors} items errored during filter evaluation`
      : undefined;

  return {
    cards,
    totalReceived,
    totalDelivered,
    receivedTrend,
    deliveredTrend,
    receivedSeries: ingestSuccessSeries,
    deliveredSeries: exportSuccessSeries,
    detailSeries,
    detailTitle,
    detailDescription,
    detailValueLabel,
    detailPrimaryValue,
    detailSecondaryLine,
    detailFootnote,
  };
};
